import React from 'react';
import { useDispatch } from 'react-redux';
import { Card, Button, Avatar, Col, Row, Tag, Space } from 'antd';
import { EditOutlined, EyeOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { WEBSITE_URL } from '../Helper';
import { getConsultantProfileData } from '../services/ConsultantService';
import { getCleaningRestorationProfileData } from '../services/CleaningRestorationService';
import './UserProfileCard.css'; // Import the CSS file for styling

const UserProfileCard = ({ profile }) => {
  const dispatch = useDispatch();
  const {
    ID,
    email_ins,
    profile_pic,
    logo_url,
    insp_fullname,
    phone_number,
    address,
    organization_name,
    profile_permalink,
    post_type,
    status = 'active', // Default status, can be 'active', 'expired', or 'canceled'
  } = profile;

  const isTrustedDealer = post_type === 'trusted_dealer';
  const isConsultant = post_type === 'consultant_profile';
  const isCleaningRestoration = post_type === 'cleaning_restoration';
  let editLink = `/inspector-profile/?inspector_id=${ID}`;
  if (isTrustedDealer) {
    editLink = `/trusted-dealer-dashboard/?dealer_id=${ID}`;
  } else if (isConsultant) {
    editLink = `/consultant-profile/?consultant_id=${ID}`;
  } else if (isCleaningRestoration) {
    editLink = `/cleaning-dashboard?profile_id=${ID}`;
  }
  const [avatarImage, setAvatarImage] = React.useState(profile_pic || logo_url || profile?.photo_url || null);

  React.useEffect(() => {
    let img = profile_pic || logo_url || profile?.photo_url;
    if (img) {
      setAvatarImage(img);
    } else if (isConsultant && ID) {
      dispatch(getConsultantProfileData(ID)).then((res) => {
        if (res && (res.profile_pic || res.photo_url)) {
          setAvatarImage(res.profile_pic || res.photo_url);
        }
      }).catch(() => {});
    } else if (isCleaningRestoration && ID) {
      dispatch(getCleaningRestorationProfileData(ID)).then((res) => {
        if (res && (res.profile_pic || res.logo_url)) {
          setAvatarImage(res.profile_pic || res.logo_url);
        }
      }).catch(() => {});
    }
  }, [profile_pic, logo_url, profile?.photo_url, isConsultant, isCleaningRestoration, ID, dispatch]);

  // Function to get status tag configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'expired':
        return { color: 'red', text: 'Expired' };
      case 'canceled':
        return { color: 'default', text: 'Canceled' };
      case 'active':
      default:
        return { color: 'green', text: 'Active' };
    }
  };

  const statusConfig = getStatusConfig(status);
  const showRenewButton = status?.toLowerCase() === 'expired';

  const handleRenew = (ID) => {
    // redirect to renew page with profile ID "product/new-location/?renewal_profile_id=54334"
    window.location.href = `${WEBSITE_URL}/product/new-location/?renewal_profile_id=${ID}`; 
  };

  return (
    <Card
      title={
        <Space>
          {insp_fullname}
          <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
        </Space>
      }
      extra={
        <Space>
          {showRenewButton && (
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={() => handleRenew(ID)}
              className="renew-button"
            >
              Renew
            </Button>
          )}
          <a href={profile_permalink} className='indpector_profile_view'>
            <Button className='profileView_icon' type="primary" icon={<EyeOutlined />} />
          </a>
          <Link to={editLink}>
            <Button className='profileEdit_icon' type="primary" icon={<EditOutlined />} />
          </Link>
        </Space>
      }
      className={`user-profile-card ${status?.toLowerCase() === 'expired' ? 'expired-profile' : ''}`}
    >
      <Row gutter={16}>
        <Col xs={24} md={24} xl={6}>
          <div className="avatar-container">
            <Avatar 
              className='profileEdit_image' 
              size={85} 
              src={avatarImage ? avatarImage : null} 
              icon={!avatarImage ? <UserOutlined /> : null}
              alt="Profile Avatar" 
            />
          </div>
        </Col>
        <Col xs={24} md={24} xl={18}>
          <div className="content-container-2">
            <p><span className='text-started'>Email:</span> {email_ins}</p>
            <p><span className='text-started'>Phone Number:</span> {phone_number}</p>
            <p><span className='text-started'>Address:</span> {address}</p>
            <p><span className='text-started'>Company:</span> {organization_name}</p>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default UserProfileCard;